import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useSession } from '../../auth';
import { trackFailed } from '../../../../../shared-web/track';
import { emberApi } from '../api';
import { sanitizeReturnTo } from '../router';
import { Link } from '../components/Link';
import { TellingDoors, entryDisclosure, entryTitle } from '../talk-sheet';
import { InboundRegistrationCTA } from '../components/InboundRegistrationCTA';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { PageLoading } from '../components/PageLoading';
import './TalkChooser.css';

export interface TalkChooserProps {
  search: URLSearchParams;
}

/**
 * The conversation entrance as a PAGE — for arrivals from outside the app.
 *
 * Inside the app this page is no longer a destination (2026-08-21): every
 * in-product door opens the same two choices as a bottom sheet over the page
 * the person is already on (`useTalkSheet`, `src/web/src/modules/ember/
 * talk-sheet.tsx`). The page stays because links do: Ember's own texts, the
 * `/topic/:slug` alias, an ad, a bookmark. Both surfaces offer exactly the
 * same two doors, so which one somebody met is never a difference in what is
 * on offer.
 *
 *   1. Chat in this browser — the web text conversation (`mode=chat`);
 *   2. Start a voice call — the in-browser voice call. Signed out, that is the
 *      guest telling (`/collect?trial=1`): a real call with the real
 *      interviewer, taken before there is an account. Signed in, `?call=1`
 *      dials the account's own telling page directly.
 *
 * The messaging door this page used to carry as a third card is gone
 * (2026-08-21). This page is the entrance to a telling; handing an account
 * back to its own Ember line is a different errand, and nothing about that
 * line, its number or its thread changed.
 *
 * Entry context (topic / request / campaign) rides along into whichever mode
 * is picked; content ownership is still decided by the telling itself.
 *
 * Both telling entrances spend ONE free-telling budget while signed out
 * (GUEST_FREE_TELLINGS, default 3). When it is spent, the cards' slot becomes
 * the registration state instead (v3.0 §4.5/§4.6.2): registering is one
 * prefilled text away (InboundRegistrationCTA), never the OTP /auth page —
 * and the reload after it lands back here with the entry context intact,
 * when the cards are real entrances again. The servers enforce the budget
 * either way; the frontend read only chooses what to render. While the
 * session (or the budget behind it) is still loading, render neither state —
 * cards that change a beat later read worse than cards that arrive a beat
 * late (same rule as the top nav).
 */
export function TalkChooserPage({ search }: TalkChooserProps): ReactElement {
  const { status } = useSession();
  const anonymous = status === 'anonymous';
  // null = not asked yet (or not applicable). A failed read leaves it null and
  // the cards render as ordinary telling entrances: the gate is enforced
  // server-side regardless, and a visitor who still has their free telling
  // must not be sent to the door because a telemetry-grade read timed out.
  const [gated, setGated] = useState<boolean | null>(null);
  // The refusal is a sign-in (2026-10-01 拍板): this browser's visitor row is
  // bound to an account that signed out, so the gate reads "Sign in".
  const [signedOut, setSignedOut] = useState(false);
  // Spent on the guest's messages rather than their tellings (2026-10-27 founder): the same gate, its own reason.
  const [messagesSpent, setMessagesSpent] = useState(false);
  // Home is the documented safe default (frontend-ui-standard §3): a chooser
  // reached without context (an old bookmark, a bare /collect forward) still
  // hands the call a way back, so the post-call wrap card always has one.
  const returnTo = sanitizeReturnTo(search.get('returnTo'), '') || '/';

  useEffect(() => {
    if (status !== 'anonymous') {
      setGated(null);
      return undefined;
    }
    let alive = true;
    void emberApi.tellingQuota()
      .then((quota) => {
        // v3.0 field first; requires_auth is its one-release legacy alias.
        if (!alive) return;
        setGated(quota.requires_registration === true || quota.requires_auth === true);
        setSignedOut(quota.has_account === true);
        setMessagesSpent(quota.reason === 'guest_messages_used');
      })
      .catch(() => {
        if (!alive) return;
        trackFailed('talk_gate', 'quota_unavailable');
        setGated(false);
      });
    return () => { alive = false; };
  }, [status]);

  const entryParams = new URLSearchParams();
  const entryType = search.get('entry_type');
  const entryId = search.get('entry_id');
  const conversationId = search.get('conversation_id');
  if (entryType) entryParams.set('entry_type', entryType);
  if (entryId) entryParams.set('entry_id', entryId);
  if (returnTo) entryParams.set('returnTo', returnTo);

  // A needs-more-context Ember links here with conversation_id. Only the web
  // chat can resume that exact conversation, so retain it there; the voice
  // option is a separate channel and keeps only its entry context.
  //
  // Which is why, when there IS a conversation to continue, this page shows the
  // chat door alone (2026-08-19). Offering "start a voice call" as an equal
  // option to somebody who came here to finish a telling silently began a NEW
  // one — dev measured zero successful resumes and a pile of duplicate cards.
  // My Embers now links past this page entirely (mode=chat); this branch is the
  // safety net for a link that still arrives without it.
  const chatParams = new URLSearchParams();
  if (entryType) chatParams.set('entry_type', entryType);
  if (entryId) chatParams.set('entry_id', entryId);
  chatParams.set('mode', 'chat');
  if (conversationId) chatParams.set('conversation_id', conversationId);
  // Back from the telling lands where THIS page's Back lands: the page the
  // visitor came from, else home. The chooser is a one-question transit, not
  // a screen anybody returns to — being asked "type or speak" again reads as
  // being sent back to the start, and a chooser reached without a
  // conversation_id offers a NEW telling rather than the one just left
  // (2026-08-26). Both doors also REPLACE this entry, so the browser's own
  // Back skips it the same way.
  chatParams.set('returnTo', returnTo);
  const chatTo = `/talk?${chatParams.toString()}`;

  // Waiting on the session, or on the budget behind an anonymous session.
  const settled = status !== 'loading' && (!anonymous || gated !== null);
  const spent = anonymous && gated === true;

  // The gate landing is a funnel fact even though nothing failed visually —
  // the registration state below has no denominator without it.
  useEffect(() => {
    if (settled && spent) trackFailed('talk_gate', signedOut ? 'account_signed_out' : messagesSpent ? 'guest_messages_used' : 'free_telling_used');
  }, [settled, spent, signedOut, messagesSpent]);

  return (
    <div className="ember-container talk-chooser">
      <div className="talk-chooser-toolbar">
        <PageBack returnTo={returnTo} className="talk-chooser-back" />
        <div className="ember-toolbar-actions talk-chooser-actions">
          <PageHome />
        </div>
      </div>
      <header className="talk-chooser-head">
        {/* A Request entry reaching this page directly (a deep link that
            skipped the S04 sheet) meets the same door and the same
            pre-authorization line the sheet carries (§4.4 Q02). */}
        {/* The same door as the sheet (TellingDoors): one title, one line,
            wherever a person meets it. */}
        <h1>
          {settled && spent ? (signedOut ? 'Sign in to keep going' : 'Keep going with Ember')
            : conversationId ? 'How would you like to continue?'
              : entryTitle(entryType)}
        </h1>
        {settled && spent ? null : (
          <p className="talk-chooser-sub">
            {conversationId
              ? 'Continuing this conversation happens here in the chat.'
              : entryDisclosure(entryType)}
          </p>
        )}
      </header>

      {settled && spent ? (
        /* The free budget is spent: registering is the next telling's door
           (v3.0 §4.5) — one prefilled text, never the OTP page. The default
           post-registration reload lands back on this URL with the entry
           context intact, and the cards above become real entrances again. */
        <div className="talk-chooser-register" data-log-seen="talk_quota_gate" data-log-state={signedOut ? 'chooser_sign_in' : 'chooser'}>
          {signedOut ? (
            <>
              <p className="talk-chooser-register-copy">
                This browser belongs to an Ember account. Sign in with one text
                and pick up where you left off.
              </p>
              <InboundRegistrationCTA logName="talk_quota_sign_in" label="Sign in by text" />
            </>
          ) : (
            <>
              <p className="talk-chooser-register-copy">
                Join with one text to keep going &mdash; and to keep what you&rsquo;ve told.
              </p>
              <InboundRegistrationCTA logName="talk_quota_register" />
            </>
          )}
        </div>
      ) : settled && conversationId ? (
        <ul className="talk-chooser-list">
          <ChooserCard
            to={chatTo}
            dataLog="talk_choose_message"
            copy="Pick up the conversation you already started, right where it stopped."
            action="Pick it back up"
            icon={<ChatIcon />}
            resume
            conversationId={conversationId}
          />
        </ul>
      ) : settled ? (
        <TellingDoors
          className="talk-chooser-doors"
          request={{ entryType: entryType || undefined, entryId: entryId || undefined, returnTo }}
        />
      ) : (
        /* The cards' slot holds the shared wait state until the session (and a
           guest's budget behind it) settles — never a blank block, and still
           never cards that change a beat after they render (UI standard §5). */
        <PageLoading variant="inline" className="talk-chooser-loading" />
      )}
    </div>
  );
}

interface ChooserCardProps {
  to: string;
  dataLog: string;
  copy: string;
  action: string;
  icon: ReactNode;
  /**
   * Whether the page arrived with a conversation to continue. Recorded on
   * BOTH doors, not just the one that can resume: the question this page has
   * to answer is what somebody picks when they came here to finish a telling,
   * and only the chat door carries the conversation onward (voice is a
   * separate channel that starts a new one).
   */
  resume?: boolean;
  conversationId?: string;
}

function ChooserCard({
  to, dataLog, copy, action, icon, resume, conversationId,
}: ChooserCardProps): ReactElement {
  const logAttrs = resume === undefined ? {} : {
    'data-log-resume': resume ? 'true' : 'false',
    ...(conversationId ? { 'data-log-conversation-id': conversationId } : {}),
  };
  const inner = (
    <>
      <span className="talk-chooser-icon" aria-hidden>{icon}</span>
      <span className="talk-chooser-body">
        <span className="talk-chooser-title">
          {action}
        </span>
        <span className="talk-chooser-copy">{copy}</span>
      </span>
      <span className="talk-chooser-arrow" aria-hidden>→</span>
    </>
  );

  const className = 'talk-chooser-card talk-chooser-compact';
  // The door REPLACES this entry rather than pushing one: the chooser asks a
  // single question, and once it is answered nobody should land back on it —
  // not by the telling's own Back, and not by the browser's.
  return (
    <li>
      <Link to={to} replace className={className} data-log={dataLog} {...logAttrs}>{inner}</Link>
    </li>
  );
}

function ChatIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a8 8 0 0 1-8 8H5l-2 2V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8Z" />
    </svg>
  );
}
